import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBRow, MDBCol, MDBCard, MDBCardBody, MDBIcon } from 'mdb-react-ui-kit'
import moment from 'moment'
import HolidayStore from '../../store/Holiday.store'

import './YearCalendar.component.scss'

/**
 * Quarter definitions for grouping holidays.
 */
const QUARTERS = [
  { id: 'Q1', label: 'Q1', months: 'Jan - Mar', monthRange: [0, 1, 2] },
  { id: 'Q2', label: 'Q2', months: 'Apr - Jun', monthRange: [3, 4, 5] },
  { id: 'Q3', label: 'Q3', months: 'Jul - Sep', monthRange: [6, 7, 8] },
  { id: 'Q4', label: 'Q4', months: 'Oct - Dec', monthRange: [9, 10, 11] },
]

/**
 * Calculate relative time string for a holiday date.
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {string} Relative time string
 */
const getRelativeTime = dateStr => {
  const today = moment().startOf('day')
  const holidayDate = moment(dateStr).startOf('day')
  const diffDays = holidayDate.diff(today, 'days')

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Tomorrow'
  if (diffDays === -1) return 'Yesterday'
  if (diffDays > 0 && diffDays <= 30) return `in ${diffDays} days`
  if (diffDays > 30 && diffDays <= 60) return `in ${Math.round(diffDays / 7)} weeks`
  if (diffDays < 0 && diffDays >= -30) return `${Math.abs(diffDays)} days ago`
  return null // Don't show for dates far in future/past
}

/**
 * NextHolidayHero Component
 *
 * Displays the next upcoming holiday prominently at the top.
 */
const NextHolidayHero = observer(() => {
  const { holidays } = HolidayStore
  const today = moment().startOf('day')

  const nextHoliday = useMemo(() => {
    return holidays.find(h => moment(h.date).startOf('day').isSameOrAfter(today))
  }, [holidays, today])

  if (!nextHoliday) return null

  const daysUntil = moment(nextHoliday.date).startOf('day').diff(today, 'days')
  const isToday = daysUntil === 0

  return (
    <div className='next-holiday-hero'>
      <div className='hero-content'>
        <div className='hero-label'>
          <MDBIcon icon='star' className='hero-icon' />
          {isToday ? 'Today is a Holiday!' : 'Next Holiday'}
        </div>
        <div className='hero-title'>{nextHoliday.summary}</div>
        <div className='hero-details'>
          <span className='hero-date'>{moment.utc(nextHoliday.startsAt).format('dddd, MMMM D, YYYY')}</span>
          {!isToday && (
            <span className='hero-countdown'>
              <MDBIcon icon='clock' className='countdown-icon' />
              {daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days away`}
            </span>
          )}
        </div>
      </div>
      <div className='hero-decoration'>
        <div className='decoration-circle' />
        <div className='decoration-circle secondary' />
      </div>
    </div>
  )
})

/**
 * HolidayCard Component
 *
 * Renders a single holiday item in the timeline.
 */
const HolidayCard = ({ holiday, showRelativeTime }) => {
  const relativeTime = showRelativeTime ? getRelativeTime(holiday.date) : null
  const isPast = moment(holiday.date).startOf('day').isBefore(moment().startOf('day'))

  return (
    <div className={`holiday-card ${isPast ? 'past' : ''}`}>
      <div className='holiday-date-badge'>
        <span className='badge-day'>{moment.utc(holiday.startsAt).format('D')}</span>
        <span className='badge-month'>{moment.utc(holiday.startsAt).format('MMM')}</span>
      </div>
      <div className='holiday-details'>
        <div className='holiday-name'>{holiday.summary}</div>
        <div className='holiday-meta'>
          <span className='holiday-day-of-week'>{holiday.dayOfWeek}</span>
          {relativeTime && <span className='holiday-relative-time'>{relativeTime}</span>}
        </div>
      </div>
    </div>
  )
}

/**
 * QuarterSection Component
 *
 * Renders a quarter's worth of holidays with a header.
 */
const QuarterSection = observer(({ quarter, holidays }) => {
  const holidayCount = holidays.length

  if (holidayCount === 0) {
    return (
      <div className='quarter-section empty'>
        <div className='quarter-header'>
          <div className='quarter-label'>
            <span className='quarter-id'>{quarter.label}</span>
            <span className='quarter-months'>{quarter.months}</span>
          </div>
          <div className='quarter-count'>No holidays</div>
        </div>
      </div>
    )
  }

  return (
    <div className='quarter-section'>
      <div className='quarter-header'>
        <div className='quarter-label'>
          <span className='quarter-id'>{quarter.label}</span>
          <span className='quarter-months'>{quarter.months}</span>
        </div>
        <div className='quarter-count'>
          {holidayCount} {holidayCount === 1 ? 'holiday' : 'holidays'}
        </div>
      </div>
      <div className='quarter-holidays'>
        {holidays.map((holiday, idx) => (
          <HolidayCard key={`holiday-${holiday.id}-${idx}`} holiday={holiday} showRelativeTime={true} />
        ))}
      </div>
    </div>
  )
})

/**
 * YearSelector Component
 *
 * Compact year navigation with dropdown.
 */
const YearSelector = observer(() => {
  const { selectedYear, isFetching } = HolidayStore
  const currentYear = new Date().getFullYear()

  const handlePrevYear = () => {
    HolidayStore.setYear(selectedYear - 1)
  }

  const handleNextYear = () => {
    HolidayStore.setYear(selectedYear + 1)
  }

  const handleCurrentYear = () => {
    HolidayStore.setYear(currentYear)
  }

  return (
    <div className='year-selector'>
      <button className='year-nav-btn' onClick={handlePrevYear} disabled={isFetching} aria-label='Previous year'>
        <MDBIcon icon='chevron-left' />
      </button>
      <span className='year-display'>{selectedYear}</span>
      <button className='year-nav-btn' onClick={handleNextYear} disabled={isFetching} aria-label='Next year'>
        <MDBIcon icon='chevron-right' />
      </button>
      {selectedYear !== currentYear && (
        <button className='year-today-btn' onClick={handleCurrentYear} disabled={isFetching}>
          <MDBIcon icon='calendar-day' className='today-icon' />
          Current Year
        </button>
      )}
    </div>
  )
})

/**
 * HolidaySummary Component
 *
 * Shows a summary of total holidays for the year.
 */
const HolidaySummary = observer(() => {
  const { holidays, selectedYear } = HolidayStore

  return (
    <div className='holiday-summary'>
      <div className='summary-icon'>
        <MDBIcon icon='calendar-check' />
      </div>
      <div className='summary-text'>
        <span className='summary-count'>{holidays.length}</span>
        <span className='summary-label'>company holidays in {selectedYear}</span>
      </div>
    </div>
  )
})

/**
 * YearCalendar Component (Timeline View)
 *
 * Main component that renders holidays in a timeline format
 * grouped by quarter.
 */
const YearCalendar = observer(() => {
  const { holidays, isLoading, isFetching, error, selectedYear } = HolidayStore

  // Group holidays by quarter
  const holidaysByQuarter = useMemo(() => {
    const grouped = {}
    QUARTERS.forEach(q => {
      grouped[q.id] = holidays.filter(h => {
        const month = moment(h.date).month()
        return q.monthRange.includes(month)
      })
    })
    return grouped
  }, [holidays])

  if (isLoading) {
    return (
      <div className='YearCalendarComponent loading-state'>
        <div className='loading-spinner'>
          <MDBIcon icon='spinner' spin size='3x' />
          <span>Loading holidays...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='YearCalendarComponent error-state'>
        <div className='error-message'>
          <MDBIcon icon='exclamation-triangle' />
          <span>{error}</span>
          <button onClick={() => HolidayStore.fetchHolidaysForYear(selectedYear)}>Retry</button>
        </div>
      </div>
    )
  }

  return (
    <div className='YearCalendarComponent timeline-view'>
      <MDBRow>
        <MDBCol size='12' lg='8' className='mx-auto'>
          <div className='timeline-header'>
            <YearSelector />
            <HolidaySummary />
          </div>

          {selectedYear === new Date().getFullYear() && <NextHolidayHero />}

          <MDBCard className='timeline-card'>
            <MDBCardBody>
              <div className='timeline-content'>
                {QUARTERS.map(quarter => (
                  <QuarterSection key={quarter.id} quarter={quarter} holidays={holidaysByQuarter[quarter.id]} />
                ))}
              </div>
            </MDBCardBody>
          </MDBCard>

          {isFetching && (
            <div className='fetching-overlay'>
              <MDBIcon icon='spinner' spin />
              <span>Updating...</span>
            </div>
          )}
        </MDBCol>
      </MDBRow>
    </div>
  )
})

export default YearCalendar
